import type { ReactNode } from "react";
import { useMemo } from "react";
import { Input, Select, Segmented, Space, Tag, Typography } from "antd";
import type { MetricOption } from "../../Processing/types";
import { CATEGORIES } from "../constants";
import { countSourceFromMetricId } from "./CountIncludePicker";
import { aggOptionsFor, coerceAgg, type AggValue } from "./metricAggregations";

const { Text } = Typography;

interface MetricPickerProps {
  metrics: MetricOption[];
  selectedMetricId: string | null;
  activeCategory: string;
  searchQuery: string;
  metricAgg: AggValue;
  onSelectMetric: (id: string) => void;
  onCategoryChange: (cat: string) => void;
  onSearchChange: (q: string) => void;
  onAggChange: (agg: AggValue) => void;
  /** When true, renders a grouped Select dropdown (Redux-sourced) instead of the category-tag list. */
  variant?: "tags" | "dropdown";
  /** Rendered between the metric select and the aggregation control. The count filter
   *  goes here, so every widget reads metric -> Counting -> Aggregation. */
  countSlot?: ReactNode;
}

/**
 * Shared metric selection UI used by both PlotConfigurator and TableConfigurator.
 *
 * `variant="tags"` (default): category tags + search input + scrollable metric list.
 * `variant="dropdown"`: a single grouped Select dropdown (for use with Redux metric data).
 */
export function MetricPicker({
  metrics,
  selectedMetricId,
  activeCategory,
  searchQuery,
  metricAgg,
  onSelectMetric,
  onCategoryChange,
  onSearchChange,
  onAggChange,
  variant = "tags",
  countSlot,
}: MetricPickerProps) {
  const allowedAggs = metrics.find((m) => m.id === selectedMetricId)?.aggregations;
  const selectMetric = (id: string) => {
    onSelectMetric(id);
    // A helmet count has no best: the backend rejects it on save.
    const allowed = metrics.find((m) => m.id === id)?.aggregations;
    if (allowed && coerceAgg(metricAgg, allowed) !== metricAgg) onAggChange(coerceAgg(metricAgg, allowed));
  };

  const filteredMetrics = useMemo(() => {
    if (variant !== "tags") return [];
    let list = metrics.filter((m) => m.category === activeCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((m) => m.name.toLowerCase().includes(q));
    }
    return list;
  }, [metrics, activeCategory, searchQuery, variant]);

  if (variant === "dropdown") {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Select
          options={buildDropdownGroups(metrics)}
          placeholder="Choose Metric"
          style={{ width: "100%" }}
          size="small"
          onChange={(value: string) => selectMetric(value)}
          value={selectedMetricId ?? undefined}
          showSearch
          filterOption={(input: string, option: any) =>
            option?.label?.toLowerCase()?.includes(input.toLowerCase()) || false
          }
        />
        {countSlot}
        {selectedMetricId != null && (
          <AggregationControl value={metricAgg} onChange={onAggChange} allowed={allowedAggs}
            isCount={countSourceFromMetricId(selectedMetricId) !== null} />
        )}
      </Space>
    );
  }

  return (
    <div className="awm-step-body">
      <Space size={4} wrap>
        {CATEGORIES.map((cat) => (
          <Tag.CheckableTag
            key={cat}
            checked={activeCategory === cat}
            onChange={() => onCategoryChange(cat)}
          >
            {cat}
          </Tag.CheckableTag>
        ))}
      </Space>

      <Input
        size="small"
        placeholder="Search metrics…"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        allowClear
      />

      <div className="awm-metric-scroll">
        {filteredMetrics.map((m) => (
          <div
            key={m.id}
            className={`awm-metric-row${selectedMetricId === m.id ? " selected" : ""}`}
            onClick={() => selectMetric(m.id)}
          >
            <Text style={{ fontSize: 12 }}>{m.name}</Text>
          </div>
        ))}
        {filteredMetrics.length === 0 && (
          <Text type="secondary" style={{ fontSize: 11, padding: 8 }}>
            No metrics found
          </Text>
        )}
      </div>

      {countSlot}
      <AggregationControl value={metricAgg} onChange={onAggChange} allowed={allowedAggs}
        isCount={countSourceFromMetricId(selectedMetricId) !== null} />
    </div>
  );
}

function AggregationControl({
  value,
  onChange,
  isCount,
  allowed,
}: {
  value: AggValue;
  onChange: (v: AggValue) => void;
  isCount: boolean;
  /** The aggregations the metric allows. Absent: every one. */
  allowed?: string[];
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Text style={{ fontSize: 12 }}>Aggregation</Text>
      {/* A count has no average or best — the backend accepts only the total — so it
          reads as fixed text rather than a choice that cannot be made. */}
      {isCount ? (
        <>
          <span className="awm-count-fixed-agg">Total</span>
          <Text type="secondary" style={{ fontSize: 11 }}>
            a count has no average
          </Text>
        </>
      ) : (
        <Segmented
          size="small"
          options={aggOptionsFor(allowed)}
          onChange={(v) => onChange(v as AggValue)}
          value={value}
        />
      )}
    </div>
  );
}

function buildDropdownGroups(metrics: MetricOption[]) {
  const grouped = new Map<string, MetricOption[]>();
  for (const m of metrics) {
    const list = grouped.get(m.category) ?? [];
    list.push(m);
    grouped.set(m.category, list);
  }
  return Array.from(grouped.entries()).map(([category, items]) => ({
    label: category,
    options: items.map((m) => ({ label: m.name, value: m.id })),
  }));
}
